/**
 * Background Jobs Store
 * Управление активными фоновыми процессами через WebSocket
 */

import { create } from 'zustand';
import { useAuthStore } from './authStore';

// wb_reference_sync / ozon_reference_sync — догрузка справочника характеристик
// площадки с экрана «Характеристики»: тип объявлен здесь, иначе экран не может
// отличить свою задачу от чужой и показать по ней прогресс.
// marketplace_card_publish — выгрузка карточек на маркетплейсы (тип заводит
// wildberries-service, см. PUBLISH_PRODUCT_CARDS_JOB_TYPE): по нему шапка
// приложения узнаёт задачу и открывает окно выгрузки вместо общего прогресса.
// marketplace_bundle_apply/import — применение и локальный импорт склеек.
// site_cards_campaign — догоняющая кампания по выборке экрана «Карточки Сайт»
// (marketplace-service); она же ставит внутри себя задачи site_cards_creation.
// site_cards_creation — пачка карточек сайта из каталога товаров (design-service,
// CreateSiteCardsUseCase): прогресс показывает общий агрегатор пайплайна.
// marketplace_card_change_detector — пересчёт расхождений ПИМ/площадка на экране
// «Изменения карточек МП» (POST /api/v1/cards/changes/detect, services/marketplace;
// имя типа задаёт бэкенд — MARKETPLACE_CARD_CHANGE_DETECTOR_JOB_TYPE в
// marketplace-card-changes-detector.service.ts, §5 ТЗ).
// marketplace_card_changes_apply — отправка подтверждённого набора на площадку
// (POST /api/v1/cards/changes/apply, services/marketplace).
// ym_sync — синхронизация зеркала каталога Я.Маркет (POST
// /api/v1/yandex-market/sellers/:sellerId/sync/catalog, services/yandex-market,
// ym-catalog.actions.ts): кнопка «Синхронизировать» на «Карточки Я.Маркет».
// ym_reference_sync — догрузка справочника Маркета (дерево категорий и их
// характеристик) на вкладке «Я.Маркет» в категориях — по образцу
// wb_reference_sync/ozon_reference_sync выше.
// marketplace_source_change_expand — разворот записи журнала правок в наборы
// изменений (POST /api/v1/cards/changes/journal/expand, services/marketplace;
// см. contract-journal-v1.md §3): кнопка «Развернуть» на вкладке «Правки»
// экрана «Изменения карточек МП».
// marketplace_source_change_apply — отправка всех наборов одной правки журнала
// одним подтверждением (POST /api/v1/cards/changes/journal/apply) — кнопка
// «Отправить по правке» там же.
// marketplace_source_change_batch_expand / _batch_apply — то же самое, но для
// ПАРТИИ массовой перегенерации визуалов целиком (Этап 5, §6.2 ТЗ журнала,
// «Массовая перегенерация — одна строка на партию», задача Q3): кнопки
// «Развернуть»/«Отправить» карточки партии (`JournalBatchModal.tsx`), POST
// .../journal/batches/:batchRef/expand и /apply — тип задачи задаёт бэкенд
// (`MARKETPLACE_SOURCE_CHANGE_BATCH_EXPAND_JOB_TYPE`/`_BATCH_APPLY_JOB_TYPE` в
// services/marketplace/src/services/marketplace-source-change-{expand,apply}.service.ts).
// marketplace_photo_resend — «Переотправить фото» (задача B2,
// SPEC_B2_photo_resend.md): POST /api/v1/cards/photos/resend, кнопка на
// вкладке «Карточки МП» карточки макета, в каталоге товаров и общая модалка
// `components/marketplace/PhotoResendModal.tsx`. Тип задаёт бэкенд
// (`MARKETPLACE_PHOTO_RESEND_JOB_TYPE` в
// services/marketplace/src/services/marketplace-photo-resend.service.ts).
// ym_category_tnved_apply — массовая простановка ТН ВЭД и документов
// соответствия Маркета по категориям (SPEC_YM_DOCUMENTS.md §4.6): кнопка
// «Отправить ТН ВЭД и документы в карточки» на «Сертификаты»
// (`features/certificates/YmMarketPanel.tsx`), POST
// /api/v1/marketplace/sellers/:sellerId/ym-category-tnved/apply. Тип задаёт
// бэкенд (`YM_CATEGORY_TNVED_JOB_TYPE` в
// services/marketplace/src/services/ym-category-tnved.service.ts).
export type JobType = 'idea_enrich' | 'design_import' | 'export' | 'product_creation' | 'wb_sync' | 'ozon_sync' | 'wb_product_creation' | 'design_completeness_check' | 'design_template_creation' | 'design_source_replacement' | 'completeness_recalculation' | 'wb_links_check' | 'ai_analysis' | 'marketplace_link' | 'wb_reference_sync' | 'ozon_reference_sync' | 'marketplace_card_publish' | 'marketplace_bundle_apply' | 'marketplace_bundle_import' | 'media_generation' | 'prod_sync' | 'storage_file_sync' | 'production_files_backfill' | 'price_index_calculate' | 'price_index_import' | 'price_index_export' | 'product_images_update' | 'site_cards_creation' | 'site_cards_campaign' | 'marketplace_card_change_detector' | 'marketplace_card_changes_apply' | 'ym_sync' | 'ym_reference_sync' | 'marketplace_source_change_expand' | 'marketplace_source_change_apply' | 'marketplace_source_change_batch_expand' | 'marketplace_source_change_batch_apply' | 'marketplace_photo_resend' | 'marketplace_card_window_edit' | 'ym_category_tnved_apply' | 'slide_insert_apply' | 'slide_insert_send' | 'slide_insert_disable' | 'slide_insert_recalc' | 'slide_insert_replace_image' | 'slide_insert_update' | 'slide_insert_exclude' | 'slide_insert_include' | 'slide_insert_preview' | 'analytics_backfill' | 'analytics_supply_calc' | 'analytics_fin_reclassify' | 'analytics_alerts_run';
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'stopped';

export interface JobStep {
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: { current: number; total: number };
}

export interface BackgroundJob {
  jobId: string;
  userId: number;
  type: JobType;
  status: JobStatus;
  name: string;
  currentStep: string;
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  steps: JobStep[];
  startedAt: Date | string;
  completedAt?: Date | string | null;
  error?: string;
  metadata?: Record<string, any>;
  isStuck?: boolean;
  lastUpdateTime?: Date;
  cancelled?: boolean;
  // Информация о пользователе (для админов)
  userName?: string;
  userEmail?: string;
}

type DeployCallback = (commit: string) => void;
const deployListeners = new Set<DeployCallback>();
const ACCOUNT_DEACTIVATED_LOGIN_URL = '/login?reason=account_deactivated';

function handleAccountDeactivated(): void {
  useAuthStore.getState().logout();
  window.location.assign(ACCOUNT_DEACTIVATED_LOGIN_URL);
}

export function onDeployComplete(cb: DeployCallback): () => void {
  deployListeners.add(cb);
  return () => { deployListeners.delete(cb); };
}

interface BackgroundJobsState {
  jobs: Map<string, BackgroundJob>;
  isConnected: boolean;
  ws: WebSocket | null;
  reconnectAttempts: number;
  
  // Actions
  addJob: (job: BackgroundJob) => void;
  updateJob: (jobId: string, updates: Partial<BackgroundJob>) => void;
  removeJob: (jobId: string) => void;
  cancelJob: (jobId: string) => Promise<void>;
  getActiveJobs: () => BackgroundJob[];
  getAllJobs: () => BackgroundJob[];
  connect: (token: string) => void;
  disconnect: () => void;
  checkStuckJobs: () => void;
  loadActiveJobs: () => Promise<void>;
}

// WebSocket джоб ВСЕГДА идёт через текущий origin: и локально (:3003), и на
// сервере nginx приложения проксирует /ws/jobs → websocket-gateway:3050.
// Абсолютный ws://host:port в env захардкоживал порт (был :3030, где маршрута
// нет → 404, прогресс висел на 0%). Абсолютные override игнорируем; разрешаем
// только относительный путь (например "/ws/jobs").
const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const explicit = import.meta.env.VITE_JOBS_WS_URL;
  if (typeof explicit === 'string' && explicit.startsWith('/')) {
    return `${protocol}//${host}${explicit}`;
  }
  return `${protocol}//${host}/ws/jobs`;
};

export const useBackgroundJobsStore = create<BackgroundJobsState>((set, get) => ({
  jobs: new Map(),
  isConnected: false,
  ws: null,
  reconnectAttempts: 0,

  addJob: (job: BackgroundJob) => {
    set((state) => {
      const newJobs = new Map(state.jobs);
      
      // Нормализуем progress - убеждаемся, что это объект с правильной структурой
      let normalizedProgress = job.progress;
      if (!normalizedProgress || typeof normalizedProgress !== 'object' || !('current' in normalizedProgress)) {
        console.warn(`[BackgroundJobs] Job ${job.jobId}: Invalid progress format, normalizing to default`);
        normalizedProgress = {
          current: 0,
          total: 0,
          percentage: 0,
        };
      } else {
        // Убеждаемся, что все поля присутствуют
        normalizedProgress = {
          current: normalizedProgress.current ?? 0,
          total: normalizedProgress.total ?? 0,
          percentage: normalizedProgress.percentage ?? (normalizedProgress.total > 0 
            ? Math.round(((normalizedProgress.current ?? 0) / normalizedProgress.total) * 100)
            : 0),
        };
      }
      
      // Конвертируем строки в Date для startedAt и completedAt
      const jobWithDates: BackgroundJob = {
        ...job,
        progress: normalizedProgress,
        startedAt: typeof job.startedAt === 'string' ? new Date(job.startedAt) : job.startedAt,
        completedAt: job.completedAt == null
          ? undefined
          : (typeof job.completedAt === 'string' ? new Date(job.completedAt) : job.completedAt),
        lastUpdateTime: new Date(),
      };
      newJobs.set(job.jobId, jobWithDates);
      return { jobs: newJobs };
    });
  },

  updateJob: (jobId: string, updates: Partial<BackgroundJob>) => {
    set((state) => {
      const job = state.jobs.get(jobId);
      
      // Если задача не найдена, создаем её из updates (race condition при создании через WebSocket)
      if (!job) {
        console.warn(`[BackgroundJobs] Job ${jobId} not found for update, creating from updates`);
        
        // Проверяем что у нас есть минимально необходимые данные для создания
        if (!updates.userId || !updates.type || !updates.name) {
          console.error(`[BackgroundJobs] Cannot create job ${jobId} from updates - missing required fields`);
          return state;
        }
        
        // Создаем новую задачу из updates
        const newStatus = updates.status || 'pending';
        const newJob: BackgroundJob = {
          jobId,
          userId: updates.userId,
          type: updates.type,
          status: newStatus,
          name: updates.name,
          currentStep: updates.currentStep || updates.name,
          progress: updates.progress || { current: 0, total: 0, percentage: 0 },
          steps: updates.steps || [],
          startedAt: updates.startedAt || new Date(),
          completedAt: newStatus === 'pending' || newStatus === 'processing'
            ? undefined
            : (updates.completedAt == null
                ? undefined
                : (typeof updates.completedAt === 'string'
                    ? new Date(updates.completedAt)
                    : updates.completedAt)),
          error: updates.error,
          metadata: updates.metadata,
          cancelled: updates.cancelled || false,
          userName: updates.userName,
          userEmail: updates.userEmail,
          lastUpdateTime: new Date(),
        };
        
        const newJobs = new Map(state.jobs);
        newJobs.set(jobId, newJob);
        return { jobs: newJobs };
      }

      // Если задача отменена, не позволяем обновлять ее статус обратно в processing/pending
      if (job.cancelled && updates.status) {
        const newStatus = updates.status;
        if (newStatus === 'processing' || newStatus === 'pending') {
          return state;
        }
      }

      // Обрабатываем progress - если передан объект, используем его, иначе сохраняем текущий
      let updatedProgress = job.progress;
      if (updates.progress) {
        if (typeof updates.progress === 'object' && 'current' in updates.progress) {
          // Это объект progress
          updatedProgress = {
            current: updates.progress.current ?? job.progress.current ?? 0,
            total: updates.progress.total ?? job.progress.total ?? 0,
            percentage: updates.progress.percentage ?? (updates.progress.total > 0 
              ? Math.round(((updates.progress.current ?? 0) / updates.progress.total) * 100)
              : 0),
          };
        } else {
          // Старый формат (число) - игнорируем, используем текущий progress
          console.warn(`[BackgroundJobs] Job ${jobId}: Ignoring numeric progress update, use object format`);
        }
      }

      const updatedJob: BackgroundJob = {
        ...job,
        ...updates,
        progress: updatedProgress,
        cancelled: updates.cancelled !== undefined ? updates.cancelled : job.cancelled,
        lastUpdateTime: new Date(),
        // Конвертируем строки в Date
        startedAt: updates.startedAt ? (typeof updates.startedAt === 'string' ? new Date(updates.startedAt) : updates.startedAt) : job.startedAt,
        // A real WebSocket restart usually contains only status=processing.
        // Active states always clear terminal timestamps. For other statuses,
        // explicit null clears and undefined preserves.
        completedAt: updates.status === 'pending' || updates.status === 'processing'
          ? undefined
          : (updates.completedAt !== undefined
              ? (updates.completedAt === null
                  ? undefined
                  : (typeof updates.completedAt === 'string'
                      ? new Date(updates.completedAt)
                      : updates.completedAt))
              : job.completedAt),
      };

      const newJobs = new Map(state.jobs);
      newJobs.set(jobId, updatedJob);
      return { jobs: newJobs };
    });
  },

  removeJob: (jobId: string) => {
    set((state) => {
      const newJobs = new Map(state.jobs);
      newJobs.delete(jobId);
      return { jobs: newJobs };
    });
  },

  cancelJob: async (jobId: string) => {
    try {
      const { jobsAPI } = await import('../lib/api');
      await jobsAPI.cancelJob(jobId);
      // Job будет обновлен через WebSocket
    } catch (error) {
      console.error('Failed to cancel job:', error);
      throw error;
    }
  },

  getActiveJobs: () => {
    const state = get();
    const { user } = useAuthStore.getState();
    const isAdmin = user?.role === 'admin' || user?.role === 'owner';
    const isOwner = user?.role === 'owner';
    const currentUserId = user?.user_id;

    return Array.from(state.jobs.values()).filter(
      job => {
        const isActive = (
          job.status === 'pending' ||
          job.status === 'processing' ||
          job.status === 'stopped'
        ) && !job.cancelled;

        if (!isActive) return false;
        if (job.metadata?.internal_pipeline_child === true || job.metadata?.parent_job_id) return false;

        // admin — все, owner — всё из store (API уже отфильтровал по org), остальные — только свои
        return isAdmin || isOwner || job.userId === currentUserId;
      }
    );
  },

  getAllJobs: () => {
    const state = get();
    const { user } = useAuthStore.getState();
    const isAdmin = user?.role === 'admin' || user?.role === 'owner';
    const isOwner = user?.role === 'owner';
    const currentUserId = user?.user_id;
    const allJobs = Array.from(state.jobs.values());

    return isAdmin || isOwner ? allJobs : allJobs.filter(job => job.userId === currentUserId);
  },

  connect: (token: string) => {
    const { ws, isConnected } = get();
    
    if (isConnected && ws && ws.readyState === WebSocket.OPEN) {
      return;
    }

    // Закрываем существующее соединение если есть
    if (ws) {
      ws.close();
    }

    try {
      // Вычисляем URL динамически для правильной работы в production
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      const newWs = new WebSocket(wsUrl);

      newWs.onopen = () => {
        set({ isConnected: true, ws: newWs, reconnectAttempts: 0 });
        
        // Загружаем активные jobs при подключении
        get().loadActiveJobs();
      };

      newWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'system:deploy_complete') {
            const commit = data.data?.commit || '';
            deployListeners.forEach(cb => { try { cb(commit); } catch {} });
            return;
          }

          if (data.type === 'job:created' || data.type === 'job:progress' || data.type === 'job:completed' || data.type === 'job:failed' || data.type === 'job:cancelled') {
            const jobData = data.data;
            if (jobData && jobData.jobId) {
              // Нормализуем progress из WebSocket
              const wsProgress = jobData.progress || {};
              const normalizedProgress = {
                current: wsProgress.current ?? 0,
                total: wsProgress.total ?? 0,
                percentage: wsProgress.percentage ?? (wsProgress.total > 0 
                  ? Math.round(((wsProgress.current ?? 0) / wsProgress.total) * 100)
                  : 0),
              };
              
              // Конвертируем job в правильный формат
              const job: BackgroundJob = {
                jobId: jobData.jobId,
                userId: jobData.userId,
                type: jobData.type,
                status: jobData.status,
                name: jobData.name,
                currentStep: jobData.currentStep || jobData.name,
                progress: normalizedProgress,
                steps: jobData.steps || [],
                startedAt: jobData.startedAt,
                completedAt: jobData.completedAt,
                error: jobData.error,
                metadata: jobData.metadata,
                cancelled: jobData.cancelled || false,
                // Информация о пользователе (для админов)
                userName: jobData.userName,
                userEmail: jobData.userEmail,
              };

              const existingJob = get().jobs.get(job.jobId);
              if (existingJob) {
                get().updateJob(job.jobId, job);
              } else {
                get().addJob(job);
              }
              
              // Автоматически удаляем завершённые/отменённые задачи через 5 секунд
              if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
                const removalDelay = job.metadata?.pipeline_mode ? 60_000 : 5_000;
                setTimeout(() => {
                  get().removeJob(job.jobId);
                }, removalDelay);
              }
            }
          }
        } catch (error) {
          console.error('[BackgroundJobs] Error parsing WebSocket message:', error);
        }
      };

      newWs.onerror = (error) => {
        console.error('[BackgroundJobs] WebSocket error:', error);
      };

      newWs.onclose = (event) => {
        set({ isConnected: false, ws: null });

        if (event.code === 4401) {
          handleAccountDeactivated();
          return;
        }
        
        // Автоматическое переподключение
        const { reconnectAttempts } = get();
        if (reconnectAttempts < 5) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
          setTimeout(() => {
            const currentToken = useAuthStore.getState().token;
            if (currentToken) {
              set({ reconnectAttempts: reconnectAttempts + 1 });
              get().connect(currentToken);
            }
          }, delay);
        } else {
          console.error('[BackgroundJobs] Max reconnection attempts reached');
        }
      };
    } catch (error) {
      console.error('[BackgroundJobs] Error creating WebSocket:', error);
    }
  },

  disconnect: () => {
    const { ws } = get();
    if (ws) {
      ws.close();
      set({ ws: null, isConnected: false, reconnectAttempts: 0 });
    }
  },

  loadActiveJobs: async () => {
    try {
      const { jobsAPI } = await import('../lib/api');
      const response = await jobsAPI.getActiveJobs();
      if (response.data?.success && Array.isArray(response.data.data)) {
        const activeJobs = response.data.data as any[];
        activeJobs.forEach(jobData => {
          const job: BackgroundJob = {
            jobId: jobData.jobId,
            userId: jobData.userId,
            type: jobData.type,
            status: jobData.status,
            name: jobData.name,
            currentStep: jobData.currentStep || jobData.name,
            progress: {
              current: jobData.progress?.current || 0,
              total: jobData.progress?.total || 0,
              percentage: jobData.progress?.percentage || 0,
            },
            steps: jobData.steps || [],
            startedAt: jobData.startedAt,
            completedAt: jobData.completedAt,
            error: jobData.error,
            metadata: jobData.metadata,
            cancelled: jobData.cancelled || false,
            // Информация о пользователе (для админов)
            userName: jobData.userName,
            userEmail: jobData.userEmail,
          };
          
          // Проверяем, существует ли уже задача с таким jobId, чтобы избежать дублирования
          const existingJob = get().jobs.get(job.jobId);
          if (existingJob) {
            // Обновляем существующую задачу, если данные из БД более свежие
            get().updateJob(job.jobId, job);
          } else {
            // Добавляем новую задачу только если её нет
            get().addJob(job);
          }
        });
      }
    } catch (error) {
      console.error('[BackgroundJobs] Failed to load active jobs:', error);
    }
  },

  checkStuckJobs: () => {
    const state = get();
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    state.jobs.forEach((job, jobId) => {
      if (job.status === 'processing' && job.lastUpdateTime) {
        const timeSinceUpdate = now - job.lastUpdateTime.getTime();
        if (timeSinceUpdate > fiveMinutes) {
          console.warn(`[BackgroundJobs] Job ${jobId} appears to be stuck (no updates for ${Math.round(timeSinceUpdate / 1000)}s)`);
          get().updateJob(jobId, { isStuck: true });
        }
      }
    });
  },
}));

// Периодическая проверка застрявших jobs (с защитой от дублей при hot reload)
if (!(globalThis as any).__pim_stuck_jobs_interval) {
  (globalThis as any).__pim_stuck_jobs_interval = setInterval(() => {
    useBackgroundJobsStore.getState().checkStuckJobs();
  }, 60000);
}
